import { Check, X, Zap, Lock, AlertTriangle, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";
import { useEvents } from "@/hooks/useConflictData";
import { useLocalCurrency } from "@/hooks/useLocalCurrency";
import PricingDialog from "@/components/PricingDialog";
import { trackConversion } from "@/lib/trackConversion";
import { useGuestCheckout } from "@/hooks/useGuestCheckout";

const features = [
  { name: "Map Events", free: "Last 24h, max 20", premium: "Full history, unlimited" },
  { name: "News Feed", free: "5 articles, 1h delay", premium: "Unlimited, real-time" },
  { name: "Push Notifications", free: "No", premium: "Instant alerts for missiles, air raids, explosions", locked: true, isNew: true },
  { name: "US Military Bases", free: "Locked", premium: "Full access", locked: true },
  { name: "Real-time Alerts", free: "No", premium: "Missile & Air Raid alerts" },
  { name: "Advanced Filters", free: "No", premium: "By country, date range" },
  { name: "Data Refresh", free: "Every 5 min", premium: "Every 30 sec" },
  { name: "Location Watchlist", free: "Not available", premium: "Up to 5 custom locations with alert focus", locked: true },
  { name: "AI Conflict Assistant", free: "Not available", premium: "Explain events, plan kits, get summaries", locked: true, isNew: true },
  { name: "Risk Index & Patterns", free: "Basic view", premium: "Risk levels and 7–30d trends for your areas", locked: true },
];

const PremiumBanner = () => {
  const { isPremium } = useAuth();
  const [pricingOpen, setPricingOpen] = useState(false);
  const { formatPriceWithEur } = useLocalCurrency();
  const { startCheckout, loading: checkoutLoading } = useGuestCheckout();
  const { data: allEvents } = useEvents(undefined, true);
  const totalCount = allEvents?.length || 134;

  if (isPremium) return null;

  return (
    <section className="border-b border-border">
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto max-w-3xl"
        >
          <div className="mb-8 text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5">
              <Zap className="h-3.5 w-3.5 text-primary" />
              <span className="font-mono text-xs font-semibold uppercase tracking-wider text-primary">
                Why Premium?
              </span>
            </div>
            <h2 className="mb-2 text-2xl font-bold text-foreground md:text-3xl">
              Unlock the Full Picture
            </h2>
            <p className="mb-1 text-sm font-medium text-warning">
              <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
              Right now you're seeing only 200 events. There are {totalCount > 200 ? totalCount : 500}+ in the last 24 h alone.
            </p>
            <p className="text-sm text-muted-foreground">
              In fast-moving conflicts, a limited view means missed patterns. Premium unlocks the full picture for your area — every event, every alert.
            </p>
          </div>

          {/* Desktop: table layout */}
          <div className="hidden sm:block overflow-hidden rounded-xl border border-border bg-card">
            <div className="grid grid-cols-3 border-b border-border bg-secondary/50 px-4 py-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Feature</span>
              <span className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Free</span>
              <span className="text-center text-xs font-semibold uppercase tracking-wider text-primary">Premium</span>
            </div>
            {features.map((f, i) => (
              <div
                key={f.name}
                className={`grid grid-cols-3 items-center px-4 py-3 ${
                  i < features.length - 1 ? "border-b border-border" : ""
                }`}
              >
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  {f.locked && <Lock className="h-3 w-3 text-muted-foreground" />}
                  {f.name}
                  {"isNew" in f && f.isNew && (
                    <span className="rounded bg-primary/20 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-primary">New</span>
                  )}
                </div>
                <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                  {f.free === "No" ? (
                    <X className="h-3.5 w-3.5 text-destructive" />
                  ) : f.free === "Locked" ? (
                    <Lock className="h-3.5 w-3.5 text-destructive" />
                  ) : (
                    <span>{f.free}</span>
                  )}
                </div>
                <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-primary">
                  <Check className="h-3.5 w-3.5" />
                  {f.premium}
                </div>
              </div>
            ))}
          </div>

          {/* Mobile: stacked cards */}
          <div className="sm:hidden space-y-2">
            {features.map((f) => (
              <div key={f.name} className="rounded-lg border border-border bg-card p-3">
                <div className="flex items-center gap-2 mb-2">
                  {f.locked && <Lock className="h-3 w-3 text-muted-foreground" />}
                  <span className="text-sm font-semibold text-foreground">{f.name}</span>
                  {"isNew" in f && f.isNew && (
                    <span className="rounded bg-primary/20 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-primary">New</span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="text-[10px] font-semibold uppercase text-muted-foreground/60">Free:</span>
                    {f.free === "No" ? (
                      <X className="h-3 w-3 text-destructive" />
                    ) : f.free === "Locked" ? (
                      <Lock className="h-3 w-3 text-destructive" />
                    ) : (
                      <span>{f.free}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
                    <Check className="h-3 w-3" />
                    <span className="text-right">{f.premium}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* AI Conflict Assistant highlight box */}
          <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-5">
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded bg-primary/20 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-primary">New</span>
              <h3 className="text-sm font-bold text-foreground">AI Conflict Assistant (Premium)</h3>
            </div>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                Explain complex events in simple language
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                Summarize last 24h in your region
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                Help you plan 72-hour kits using our checklists
              </li>
            </ul>
          </div>

          {/* CTA — single monthly price anchor */}
          <div className="mt-6 flex flex-col items-center text-center">
            <p className="mb-3 text-xs font-medium text-warning/90">
              ⚡ Limited time pricing — future plans may increase. Secure your rate now.
            </p>
            <Button
              size="lg"
              className="gap-2 font-semibold rounded-full w-auto"
              onClick={() => { trackConversion("banner_cta_clicked"); startCheckout("monthly"); }}
              disabled={checkoutLoading}
            >
              <Zap className="h-4 w-4" />
              Don't miss critical events — {formatPriceWithEur(2.99)}/mo
            </Button>
            <button
              onClick={() => { trackConversion("banner_see_all_plans"); setPricingOpen(true); }}
              className="mt-2 text-xs text-primary hover:underline"
            >
              See all plans & save up to 44%
            </button>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Cancel anytime • Instant access • Secure payment via Stripe
            </p>
          </div>
        </motion.div>
      </div>

      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </section>
  );
};

export default PremiumBanner;
